import { Participant, Track } from 'livekit-client';
import { computedFn } from 'mobx-utils';
import { participantsSortStore, participantsStore } from 'state/store';
import { selectHasRecorder } from './room';

export const selectLocalParticipant = () => {
    return participantsStore.localParticipant;
};

export const selectRemoteParticipants = () => {
    return participantsStore.remoteParticipants;
};

export const selectRemoteParticipantById = (id: string) => {
    return participantsStore.remoteParticipants.get(id) ?? null;
};

export const selectParticipants = computedFn(() => {
    const hasRecorder = selectHasRecorder();
    const remoteParticipants = selectRemoteParticipants();
    const remotes = Array.from(remoteParticipants.values());
    /**
     * Если ведём запись, то не отображаем фиктивного локального пользователя
     * от имени которого ведётся запись.
     */
    let participants = [] as Participant[];
    if (!hasRecorder) {
        const localParticipant = selectLocalParticipant();
        participants = localParticipant ? [localParticipant] : [];
    }
    participants.push(...remotes);

    return participants;
});

export const selectIsScreenShared = () => {
    return participantsStore.isScreenShareEnabled;
};

export const selectScreenShareParticipants = () => {
    return participantsStore.screenShareParticipants;
};

export const selectFirstScreenSharingParticipant = () => {
    if (!selectIsScreenShared()) return null;

    const selectedScreenSharingParticipants = selectScreenShareParticipants();
    const screenSharingParticipants = Array.from(selectedScreenSharingParticipants.values());
    const firstScreenSharingParticipant = screenSharingParticipants[0] || null;

    return firstScreenSharingParticipant;
};

export const selectFirstScreenSharingTrack = () => {
    const firstScreenSharingParticipant = selectFirstScreenSharingParticipant();

    if (!firstScreenSharingParticipant) return null;

    const track = firstScreenSharingParticipant.getTrackPublication(Track.Source.ScreenShare);
    if (track?.isSubscribed && track.videoTrack) {
        return track.videoTrack;
    }

    return null;
};

export const selectIsParticipantSharingScreen = computedFn((participant: Participant) => {
    const screenSharedParticipants = selectScreenShareParticipants();
    return screenSharedParticipants.has(participant.identity);
});

export const selectParticipantsSortOrder = () => {
    return participantsSortStore.sortOrder;
};

export const selectFixedSlotsCount = () => {
    return participantsSortStore.fixedSlotsCount;
};

export const selectRecentSpeakerId = () => {
    return participantsSortStore.recentSpeakerId;
};

export const selectRecentSpeaker = () => {
    const recentSpeakerId = selectRecentSpeakerId();

    if (!recentSpeakerId) return null;

    const localParticipant = selectLocalParticipant();
    if (localParticipant?.identity === recentSpeakerId) {
        return localParticipant;
    }

    const remoteParticipants = selectRemoteParticipants();
    if (remoteParticipants.has(recentSpeakerId)) {
        return remoteParticipants.get(recentSpeakerId);
    }

    return null;
};
